import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { vc, formatNum, Loading, Avatar } from "./common";
import { toast } from "sonner";
import { inviteUrl } from "../lib/config";
import { AreaChart, Area, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Coins, Clock, TrendingUp, Gift, Wallet as WalletIcon, Star, Check, ArrowUpRight, Users, Music2, Zap, Plus, ArrowDownToLine, UserPlus, Flame, Instagram, Copy, Link2 } from "lucide-react";

function Balance({ icon: Icon, label, value, primary }) {
  return (
    <div className={`rounded-2xl p-4 ${primary ? "glass glass-gold" : "surface"}`} data-testid={`dash-balance-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={17} className={primary ? "text-gold" : "text-muted-foreground"} />
      <div className={`font-mono text-2xl mt-2 ${primary ? "text-gold" : ""}`}>{vc(value)}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="surface rounded-2xl p-4 flex items-center gap-3" data-testid={`dash-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-9 h-9 rounded-xl surface-2 flex items-center justify-center text-eblue"><Icon size={17} /></div>
      <div><div className="text-lg font-semibold leading-none">{value}</div><div className="text-xs text-muted-foreground mt-1">{label}</div></div>
    </div>
  );
}

function ReferralCard({ user }) {
  const [data, setData] = useState(null);
  const isPlus = user?.plan === "plus" || user?.role === "admin";
  const inviteLink = inviteUrl(user?.username);

  useEffect(() => { api.get("/me/referrals").then(({ data }) => setData(data)).catch(() => setData(false)); }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied — share it to earn VirtuCoins!"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div data-testid="referral-dashboard">
      <div className="flex items-center gap-2 font-display font-semibold mb-3"><Gift size={17} className="text-gold" /> Referrals</div>
      {!isPlus ? (
        <div className="surface rounded-2xl p-4 flex items-center justify-between gap-3">
          <div><div className="text-sm font-semibold">Earn {data?.reward_per_referral || 5} VC per signup</div><p className="text-xs text-muted-foreground mt-0.5">Upgrade to VirtuStars+ to unlock referral rewards.</p></div>
          <Link to="/plus" data-testid="referral-upgrade-btn" className="btn-gold rounded-full px-4 h-9 text-sm font-semibold flex items-center shrink-0">Go Plus</Link>
        </div>
      ) : (
        <div className="surface rounded-2xl p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="surface-2 rounded-xl p-3" data-testid="referral-count"><div className="text-2xl font-mono">{data?.count ?? "—"}</div><div className="text-xs text-muted-foreground">People joined</div></div>
            <div className="surface-2 rounded-xl p-3" data-testid="referral-earned"><div className="text-2xl font-mono text-gold">{data ? vc(data.total_vc) : "—"}</div><div className="text-xs text-muted-foreground">VirtuCoins earned</div></div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Your invite link · {data?.reward_per_referral || 5} VC per new member</div>
            <div className="flex items-center gap-2 surface-2 border border-white/10 rounded-xl px-3 h-11">
              <Link2 size={15} className="text-eblue shrink-0" />
              <input readOnly value={inviteLink} className="bg-transparent flex-1 text-xs outline-none truncate" data-testid="referral-link-input" onFocus={(e) => e.target.select()} />
              <button onClick={copy} data-testid="referral-copy-btn" className="btn-blue rounded-lg px-3 h-8 text-xs font-semibold flex items-center gap-1 shrink-0"><Copy size={13} /> Copy</button>
            </div>
          </div>
          {data?.referrals?.length > 0 && (
            <div className="space-y-2" data-testid="referral-list">
              {data.referrals.map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Avatar src={r.user?.avatar_url} name={r.user?.display_name} size={34} />
                  <div className="min-w-0 flex-1"><Link to={`/${r.user?.username}`} className="text-sm font-medium truncate hover:text-eblue block">{r.user?.display_name || "VirtuStar"}</Link><div className="text-[11px] text-muted-foreground">@{r.user?.username}</div></div>
                  <span className="text-xs font-mono text-gold shrink-0">+{vc(r.reward)} VC</span>
                </div>
              ))}
            </div>
          )}
          {data && data.count === 0 && <p className="text-xs text-muted-foreground text-center py-1">No referrals yet — share your link to start earning.</p>}
        </div>
      )}
    </div>
  );
}

export function ProfileDashboard({ user, onShareStory }) {
  const [w, setW] = useState(null);
  const [txns, setTxns] = useState(null);
  const [sub, setSub] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setW(data)).catch(() => setW(false));
    api.get("/wallet/transactions").then(({ data }) => setTxns(data)).catch(() => setTxns([]));
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/me/insights").then(({ data }) => setInsights(data)).catch(() => {});
  }, []);

  const chartData = useMemo(() => {
    const days = 14;
    const buckets = {};
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today); d.setDate(today.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = { date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), vc: 0 };
    }
    (txns || []).forEach((t) => {
      if (t.amount > 0) {
        const key = (t.created_at || "").slice(0, 10);
        if (buckets[key]) buckets[key].vc += t.amount;
      }
    });
    return Object.values(buckets).map((b) => ({ ...b, vc: Math.round(b.vc * 100) / 100 }));
  }, [txns]);

  const earned14 = chartData.reduce((s, d) => s + d.vc, 0);
  const isPlus = user?.plan === "plus";
  const pricing = sub?.pricing || { monthly: 14.99 };

  if (w === null) return <Loading />;

  return (
    <div className="space-y-6" data-testid="profile-dashboard">
      {/* One-tap share to Instagram story */}
      {onShareStory && (
        <button onClick={onShareStory} data-testid="dash-share-story-btn"
          className="w-full group relative overflow-hidden rounded-2xl p-[1.5px] bg-gradient-to-r from-eblue via-violet-500 to-gold">
          <span className="flex items-center justify-center gap-2.5 rounded-2xl bg-[#0b0b14] px-4 py-3.5 font-semibold text-white group-hover:bg-[#0b0b14]/80 transition-colors">
            <Instagram size={18} className="text-gold" />
            Share my profile to Story
            <ArrowUpRight size={16} className="text-eblue" />
          </span>
        </button>
      )}

      {/* Wallet */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-display font-semibold"><WalletIcon size={17} className="text-gold" /> VWallet</div>          <Link to="/wallet" className="text-xs text-eblue hover:underline flex items-center gap-1" data-testid="dash-open-wallet">Manage wallet <ArrowUpRight size={13} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Balance icon={Coins} label="Available VC" value={w?.available || 0} primary />
          <Balance icon={Clock} label="Pending VC" value={w?.pending || 0} />
          <Balance icon={TrendingUp} label="Lifetime VC" value={w?.lifetime || 0} />
          <Balance icon={Gift} label="Tips Received" value={w?.tips_received || 0} />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Link to="/wallet" state={{ tab: "buy" }} data-testid="dash-buy-vc" className="btn-gold inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-semibold"><Plus size={15} /> Buy VirtuCoins</Link>
          <Link to="/wallet" state={{ tab: "withdraw" }} data-testid="dash-withdraw" className="inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-medium border border-white/15 hover:bg-white/5"><ArrowDownToLine size={15} /> Withdraw {w?.withdrawable ? `(${vc(w.withdrawable)} VC)` : ""}</Link>
        </div>
      </div>

      {/* Stats */}
      <div>
        <div className="flex items-center gap-2 font-display font-semibold mb-3"><Zap size={17} className="text-eblue" /> Your Stats</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Followers" value={formatNum(user.follower_count)} />
          <StatCard icon={Users} label="Following" value={formatNum(user.following_count)} />
          <StatCard icon={Star} label="VStar Score" value={formatNum(user.vstar_score || 0)} />
          <StatCard icon={Music2} label="Streams" value={formatNum(user.stream_count || 0)} />
        </div>
      </div>

      {/* Referrals */}
      <ReferralCard user={user} />

      {/* Earnings chart */}
      <div className="surface rounded-2xl p-5" data-testid="dash-earnings-chart">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-semibold">Earnings — last 14 days</div>
          <div className="text-sm text-gold font-mono">+{vc(earned14)} VC</div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">VirtuCoins earned from rewards, tips and payouts.</p>
        <div className="h-56 -ml-2">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={1}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d4af37" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#d4af37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#8a8a93", fontSize: 11 }} tickLine={false} axisLine={false} interval={2} />
              <YAxis tick={{ fill: "#8a8a93", fontSize: 11 }} tickLine={false} axisLine={false} width={34} />
              <Tooltip contentStyle={{ background: "#0d0d10", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#fff" }} formatter={(v) => [`${vc(v)} VC`, "Earned"]} />
              <Area type="monotone" dataKey="vc" stroke="#d4af37" strokeWidth={2} fill="url(#goldFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="dash-insights">
        <div className="surface rounded-2xl p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="font-display font-semibold flex items-center gap-2"><UserPlus size={16} className="text-eblue" /> Follower growth</div>
            {insights && <div className="text-sm text-eblue font-mono">+{insights.net_new_followers} (30d)</div>}
          </div>
          <p className="text-xs text-muted-foreground mb-4">Your follower count over the last 30 days.</p>
          <div className="h-48 -ml-2">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={1}>
              <LineChart data={insights?.follower_growth || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#8a8a93", fontSize: 10 }} tickLine={false} axisLine={false} interval={6} />
                <YAxis tick={{ fill: "#8a8a93", fontSize: 11 }} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0d0d10", border: "1px solid rgba(45,156,219,0.3)", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#fff" }} formatter={(v) => [v, "Followers"]} />
                <Line type="monotone" dataKey="followers" stroke="#2d9cdb" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface rounded-2xl p-5">
          <div className="font-display font-semibold flex items-center gap-2 mb-1"><Flame size={16} className="text-gold" /> Top content</div>
          <p className="text-xs text-muted-foreground mb-4">Your best-performing posts, videos, reels & tracks.</p>
          {insights && insights.top_content.length > 0 ? (
            <div className="space-y-2">
              {insights.top_content.map((c, i) => (
                <div key={i} className="flex items-center gap-3 surface-2 rounded-xl p-2.5" data-testid={`dash-top-${i}`}>
                  <span className="w-5 text-center text-xs font-mono text-muted-foreground">{i + 1}</span>
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/5 text-eblue shrink-0">{c.type}</span>
                  <span className="text-sm truncate flex-1">{c.title}</span>
                  <span className="text-xs text-gold font-mono shrink-0">{formatNum(c.metric)} {c.metric_label}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center">Post content and gain engagement to see what's trending for you.</div>
          )}
        </div>
      </div>
      {isPlus ? (
        <div className="glass glass-gold rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="dash-membership-active">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gold/15 flex items-center justify-center text-gold"><Star size={22} /></div>
            <div><div className="font-display font-semibold text-gold">VirtuStars+ Active</div><div className="text-xs text-muted-foreground">Premium creator tools, lower withdrawal threshold & gold badge.</div></div>
          </div>
          <Link to="/plus" className="text-xs text-eblue hover:underline">Manage plan</Link>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-2xl p-6 border border-gold/25 bg-gradient-to-br from-gold/10 to-transparent" data-testid="dash-upgrade-card">
          <div className="flex items-center gap-2 text-gold text-[11px] font-semibold tracking-widest uppercase mb-2"><Star size={13} /> Upgrade</div>
          <h3 className="font-display font-bold text-xl">Go VirtuStars+</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">Upload music & long-form TV, unlock advanced analytics, higher limits, a gold badge and a lower 50 VC withdrawal threshold.</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm">
            {["Upload Music & TV", "Advanced analytics", "Gold profile badge", "50 VC withdrawals"].map((b) => <span key={b} className="inline-flex items-center gap-1.5 text-muted-foreground"><Check size={14} className="text-gold" />{b}</span>)}
          </div>
          <Link to="/plus" data-testid="dash-upgrade-btn" className="btn-gold inline-flex items-center gap-1.5 rounded-full px-5 h-11 mt-4 font-semibold text-sm">
            Upgrade — ${pricing.monthly}/mo <ArrowUpRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
}
